// src/services/pftSurveyAnswers.js
//
// The PFT health survey's answers (pft_survey_submissions, written by pftSurveySubmit)
// in plain words: the card's Medical history section, and later the notes a move
// writes. Firebase-free and tested (pftSurveyAnswers.test.js). The wording follows Raw
// Database's (conditionDetail / buildLegacyNotes) without touching that page.

import { DIAGNOSIS_OPTIONS, surveyConditionLabel } from "../constants/diseaseOptions";
import { nameTokens, normalizeDob } from "./patientDuplicateMatch";

/** "Exacerbations last year: 2", "On GLP-1: yes", "Uses C-PAP: no", or "". */
export const surveyConditionDetail = (c) => {
  if (!c || typeof c !== "object") return "";
  if (c.exacerbations != null && c.exacerbations !== "") return `Exacerbations last year: ${c.exacerbations}`;
  if (c.glp1ForDiabetes) return `On GLP-1: ${c.glp1ForDiabetes}`;
  if (c.usingCpap) return `Uses C-PAP: ${c.usingCpap}`;
  if (c.followup) return String(c.followup);
  return "";
};

/** Each reported condition as { label, detail } (label as staff see it: ILD, not IPF). */
export const surveyConditions = (s) =>
  (Array.isArray(s && s.conditions) ? s.conditions : [])
    .filter((c) => c && c.name)
    .map((c) => ({ label: surveyConditionLabel(c.name), detail: surveyConditionDetail(c) }));

/** The weight-management answers as lines; [] when not answered. */
export const surveyWeightLines = (s) => {
  if (!s || !s.weightManagementInterest || s.weightManagementInterest === "not answered") return [];
  const lines = [`Weight management interest: ${s.weightManagementInterest}`];
  if (s.height) lines.push(`Height: ${s.height}`);
  if (s.weightLbs != null && s.weightLbs !== "") lines.push(`Weight: ${s.weightLbs} lbs`);
  if (s.bmi != null && s.bmi !== "") lines.push(`BMI (reported): ${s.bmi}`);
  if (s.onGlp1WeightMgmt) lines.push(`On GLP-1 (weight mgmt): ${s.onGlp1WeightMgmt}`);
  return lines;
};

const digits = (v) => String(v || "").replace(/\D/g, "").slice(-10);
const words = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/**
 * Does the patient's answer differ from the PFT's value? Blank on either side isn't
 * a difference. Phones compare by digits, addresses ignoring case and punctuation.
 */
export const differsFromPft = (field, surveyValue, pftValue) => {
  if (!String(surveyValue || "").trim() || !String(pftValue || "").trim()) return false;
  if (field === "phone") return digits(surveyValue) !== digits(pftValue);
  return words(surveyValue) !== words(pftValue);
};

// ---- Moving a patient who answered (decided 2026-10-23) -------------------------

// Survey condition (stored name) → the diagnosis RegX stores. "IPF" is stored and
// shown as ILD (CLAUDE.md "IPF is stored, ILD is only shown"). Lung Nodules has no
// diagnosis: it stays in the notes only.
const CONDITION_TO_DIAGNOSIS = {
  Asthma: "Asthma",
  COPD: "COPD",
  Bronchiectasis: "Bronchiectasis",
  "Pulmonary Fibrosis": "IPF",
  "Sleep Apnea": "Sleep Apnea",
  Diabetes: "Diabetes",
  Sarcoidosis: "Sarcoid",
  "Heart Disease": "Heart Disease",
  "Lung Cancer": "Cancer",
  "Cancer (other)": "Cancer",
};

/** The stored diagnosis values for the survey's conditions, in the app's usual order. */
export const surveyConditionsToDiagnosis = (conditions = []) => {
  const picked = new Set(
    (Array.isArray(conditions) ? conditions : []).map((c) => CONDITION_TO_DIAGNOSIS[c && c.name]).filter(Boolean),
  );
  return DIAGNOSIS_OPTIONS.filter((d) => picked.has(d));
};

const fmtDay = (ts) => {
  const d = ts && typeof ts.toDate === "function" ? ts.toDate() : ts ? new Date(ts) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
};

/** The medical history as note lines (heading, weight, conditions, who signed). */
export const surveyHistoryLines = (s) => {
  const when = fmtDay(s.submittedAt);
  const lines = [`— From the PFT health survey${when ? ` (answered ${when})` : ""} —`, ...surveyWeightLines(s)];
  const conds = surveyConditions(s);
  if (conds.length) {
    lines.push("Reported conditions:");
    conds.forEach((c) => lines.push(`  • ${c.label}${c.detail ? ` (${c.detail})` : ""}`));
  } else {
    lines.push("Reported conditions: none of the listed ones");
  }
  if (s.printedName) lines.push(`E-sign printed name: ${s.printedName}`);
  if (s.signedByRepresentative) lines.push(`Signed by: ${s.signerRelationshipLabel || "a representative"}`);
  return lines;
};

/**
 * What the survey changes on a NEW patient record (pftMovePlan.newPatientRecord):
 *   phone, address, email: the patient's answer wins; a blank answer keeps the PFT's.
 *   name, DOB: stay as on the PFT; a different answer is only noted.
 *   diagnosis: from the conditions. consentUrl: the signed PDF. bmi: their answer, if
 *   the record has none (like Raw Database's move).
 *   otherFiles: the signed PDF again, as a merge lists it, so replacing the main file
 *   (Files upload) can't lose it (decided 2026-10-26).
 *   notes: the medical history, plus every PFT value replaced and every answer not used.
 */
export const surveyFieldsForNewPatient = (record, survey, { by = "", now } = {}) => {
  const s = survey || {};
  const replaced = [];
  const notUsed = [];
  const pick = (field, label, pftValue) => {
    const answer = String(s[field] || "").trim();
    if (!answer) return pftValue || "";
    if (differsFromPft(field, answer, pftValue)) replaced.push(`${label} on the PFT: ${pftValue}`);
    return answer;
  };
  const phone = pick("phone", "Phone", record.phone);
  const physicalAddress = pick("address", "Address", record.physicalAddress);
  const email = String(s.email || "").trim() || record.email || "";
  if (String(s.name || "").trim() && nameTokens(s.name).sort().join(" ") !== nameTokens(record.name).sort().join(" ")) {
    notUsed.push(`Name on the survey: ${String(s.name).trim()}`);
  }
  if (normalizeDob(s.dob) && normalizeDob(s.dob) !== normalizeDob(record.dob)) {
    notUsed.push(`Date of birth on the survey: ${s.dob}`);
  }

  const lines = surveyHistoryLines(s);
  if (replaced.length) lines.push("Replaced with the patient's survey answer:", ...replaced.map((l) => `  ${l}`));
  if (notUsed.length) lines.push("Survey answers not used (the PFT's are kept):", ...notUsed.map((l) => `  ${l}`));

  const bmi = s.bmi != null && s.bmi !== "" && !String(record.bmi || "").trim() ? { bmi: String(s.bmi) } : {};
  return {
    phone,
    physicalAddress,
    email,
    diagnosis: DIAGNOSIS_OPTIONS.filter(
      (d) => (record.diagnosis || []).includes(d) || surveyConditionsToDiagnosis(s.conditions).includes(d),
    ),
    consentUrl: s.consentPdfUrl || record.consentUrl || "",
    ...(s.consentPdfUrl ? { otherFiles: [surveyConsentFile(s, { by, now })] } : {}),
    notes: [record.notes, lines.join("\n")].filter((x) => String(x || "").trim()).join("\n\n"),
    ...bmi,
    pftSurveySubmissionId: s.id || "",
  };
};

/** For the merge screen: which diagnoses the survey adds, and whether its consent fills an empty one. */
export const surveyMergeSummary = (record, survey) => {
  const current = Array.isArray(record && record.diagnosis) ? record.diagnosis : [];
  return {
    addDiagnosis: surveyConditionsToDiagnosis(survey && survey.conditions).filter((d) => !current.includes(d)),
    fillsConsent: !!(survey && survey.consentPdfUrl) && !String((record && (record.consentUrl || record.fileUrl)) || "").trim(),
  };
};

/** The "Other files" entry for a survey's signed consent. */
export const surveyConsentFile = (survey, { by = "", now = new Date().toISOString() } = {}) => {
  const when = fmtDay(survey.submittedAt);
  return {
    name: `Health survey consent${when ? ` (${when})` : ""}`,
    url: survey.consentPdfUrl,
    addedAt: now,
    addedBy: by,
    source: "PFT health survey",
    surveySubmissionId: survey.id || "",
  };
};

/**
 * What the survey adds when merging into a patient ALREADY in RegX, on top of the
 * side-by-side choices (applyMerge's profileUpdates; phone/address/email "Use survey"):
 *   diagnosis   new ones added, none removed
 *   consentUrl  only if the patient has no consent file
 *   otherFiles  the signed consent is always listed (once)
 *   bmi         only if the patient has none
 *   notes       the medical history appended, plus the RegX values the survey replaced
 *               and the survey's name / DOB when they differ (not used)
 * Returns only the fields that change.
 */
export const surveyMergeUpdates = (record, survey, plan, profileUpdates, { by = "", now = new Date().toISOString() } = {}) => {
  const r = record || {};
  const s = survey || {};
  const out = {};

  const current = Array.isArray(r.diagnosis) ? r.diagnosis : [];
  const added = surveyConditionsToDiagnosis(s.conditions).filter((d) => !current.includes(d));
  if (added.length) out.diagnosis = [...current, ...added];

  if (s.consentPdfUrl) {
    if (!String(r.consentUrl || r.fileUrl || "").trim()) out.consentUrl = s.consentPdfUrl;
    const files = Array.isArray(r.otherFiles) ? r.otherFiles : [];
    if (!files.some((f) => f && f.url === s.consentPdfUrl)) out.otherFiles = [...files, surveyConsentFile(s, { by, now })];
  }

  if (s.bmi != null && s.bmi !== "" && !String(r.bmi || "").trim()) out.bmi = String(s.bmi);

  out.notes = [r.notes, surveyMergeNotesBlock(r, s, plan, profileUpdates)].filter((x) => String(x || "").trim()).join("\n\n");

  const ids = Array.isArray(r.pftSurveySubmissionIds) ? r.pftSurveySubmissionIds : [];
  if (s.id && !ids.includes(s.id)) out.pftSurveySubmissionIds = [...ids, s.id];
  return out;
};

/** The text a merge appends to the notes: history, RegX values replaced, answers not used. */
export const surveyMergeNotesBlock = (record, survey, plan, profileUpdates) => {
  const r = record || {};
  const s = survey || {};
  const replaced = (plan.profile || [])
    .filter((row) => row.source === "survey" && row.kind === "conflict" && profileUpdates[row.field] !== undefined)
    .map((row) => `  ${row.label} in RegX before: ${row.current}`);
  const notUsed = [];
  if (String(s.name || "").trim() && nameTokens(s.name).sort().join(" ") !== nameTokens(r.name || r.fullName).sort().join(" ")) {
    notUsed.push(`  Name on the survey: ${String(s.name).trim()}`);
  }
  if (normalizeDob(s.dob) && normalizeDob(s.dob) !== normalizeDob(r.dob)) notUsed.push(`  Date of birth on the survey: ${s.dob}`);
  const block = surveyHistoryLines(s);
  if (replaced.length) block.push("Replaced with the patient's survey answer:", ...replaced);
  if (notUsed.length) block.push("Survey answers not used (RegX's are kept):", ...notUsed);
  return block.join("\n");
};

/**
 * The same additions for a Google Sheet patient (Dubois), as the `survey` part of the
 * mergePftIntoSheetPatient request. The server (functions/sheetPftMerge.js) only ever
 * ADDS: diagnoses appended to DIAGNOSIS, the notes block appended to NOTES, the consent
 * link written to FILE_URL only if empty, the PDF appended to otherFiles, BMI only if
 * empty. So this sends what to add, not the patient's whole notes or list.
 */
export const sheetSurveyAdditions = (record, survey, plan, profileUpdates, { by = "", now = new Date().toISOString() } = {}) => {
  const r = record || {};
  const s = survey || {};
  const current = Array.isArray(r.diagnosis) ? r.diagnosis : [];
  const out = {
    addDiagnosis: surveyConditionsToDiagnosis(s.conditions).filter((d) => !current.includes(d)),
    appendNotes: surveyMergeNotesBlock(r, s, plan, profileUpdates),
  };
  if (s.consentPdfUrl) {
    out.fillFileUrl = s.consentPdfUrl;
    out.addOtherFile = surveyConsentFile(s, { by, now });
  }
  if (s.bmi != null && s.bmi !== "") out.fillBmi = String(s.bmi);
  if (s.id) out.submissionId = s.id;
  if (!out.addDiagnosis.length) delete out.addDiagnosis;
  return out;
};
